/**
 * Naming convention for DataSource Link ingest tables.
 *
 * A DataSource Link's `foreignTableName` is derived from the DataSource title and
 * the SchemaDefinition name: `<sanitized datasource title>_<sanitized schema name>`.
 * This is the single source of truth shared by:
 *   - DataSource Link creation (the "Manage Data Sources" UI), and
 *   - the DataSource Adapter steps, which resolve a blob to exactly one link by
 *     recomputing the expected table name from the configured datasource + schema.
 *
 * Keep both call sites on this util so the generated and matched names never drift.
 */

/**
 * Normalize a free-text name into a table-name part: lowercase, every non `a–z`
 * character collapsed to a single underscore, and surrounding underscores
 * trimmed. Empty input falls back to `"untitled"`.
 */
export function sanitizeNamePart(s: string): string {
  const sanitized = s
    .toLowerCase()
    .replace(/[^a-z]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
  return sanitized || "untitled";
}

/**
 * The ingest `foreignTableName` for a DataSource Link binding `dataSourceTitle`
 * to `schemaName` — `<sanitized title>_<sanitized schema name>`.
 */
export function generateLinkTableName(
  dataSourceTitle: string,
  schemaName: string,
): string {
  return `${sanitizeNamePart(dataSourceTitle)}_${sanitizeNamePart(schemaName)}`;
}
